import { useEffect } from "react";
import { calendarService } from "@miplatform/api";
import { CONFIG_PAGES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useRouter } from "@miplatform/hooks";
import { CalendarEventType, ITenantHolidayResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { useCalendarSets } from "@mibuildweb/hooks";
import { CONFIG_PATHS } from "../../../../routes/config/constants";
import {
  IHolidayFormData,
  mapHolidayResponseToFormData,
} from "../ConfigHolidays.types";

const getHolidayUUID = createUUID();

export const useHolidayDetailsViewModel = ({
  reset,
}: {
  reset: (data?: Partial<IHolidayFormData>) => void;
}) => {
  const { navigate } = useRouter();
  const stateManager = useComponentState();

  const {
    calendarSets: orderCalendarSets,
    setCalendarSets: setOrderCalendarSets,
    getCalendarSets: getOrderCalendarSets,
  } = useCalendarSets({ eventType: CalendarEventType.Order });

  const {
    calendarSets: workOrderCalendarSets,
    setCalendarSets: setWorkOrderCalendarSets,
    getCalendarSets: getWorkOrderCalendarSets,
  } = useCalendarSets({
    eventType: CalendarEventType.WorkOrder,
  });

  const fetchCalendarSets = async () => {
    const [orderResponse, workOrderResponse] = await Promise.all([
      getOrderCalendarSets(),
      getWorkOrderCalendarSets(),
    ]);

    if (orderResponse?.status === HTTP_STATUSES.OK) {
      setOrderCalendarSets(orderResponse.data);
    }
    if (workOrderResponse?.status === HTTP_STATUSES.OK) {
      setWorkOrderCalendarSets(workOrderResponse.data);
    }
  };

  const getHolidayDetails = async (id: string) => {
    stateManager.abort(getHolidayUUID);
    const response = await stateManager.run(async () => {
      return calendarService.tenantHolidays.get(id);
    }, getHolidayUUID);

    if (response?.status === HTTP_STATUSES.OK) {
      const holiday: ITenantHolidayResponse = response.data;
      const holidayCalendarSets = holiday.calendarSets || [];

      const orderSets = holidayCalendarSets.filter(
        (cs) => cs.eventType === CalendarEventType.Order
      );

      const workOrderSets = holidayCalendarSets.filter(
        (cs) => cs.eventType === CalendarEventType.WorkOrder
      );

      reset(mapHolidayResponseToFormData(holiday, orderSets, workOrderSets));
    }
  };

  const handleCreateHoliday = async (data: IHolidayFormData) => {
    const payload = {
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      calendarSetIds: [
        ...data.orderCalendarSetIds,
        ...data.workOrderCalendarSetIds,
      ],
    };

    const response = await stateManager.run(async () => {
      return calendarService.tenantHolidays.create({
        data: payload,
        toastMessageConfig: {
          success: {
            text: `${data.name} created successfully.`,
          },
          failure: {
            text: `Failed to create ${data.name}. Please try again.`,
          },
        },
      });
    });

    if (response?.status === HTTP_STATUSES.OK) {
      navigate(CONFIG_PATHS[CONFIG_PAGES.HOLIDAYS]);
    }
  };

  const handleUpdateHoliday = async (data: IHolidayFormData) => {
    const payload = {
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      modifiedOn: data.modifiedOn,
      calendarSetIds: [
        ...data.orderCalendarSetIds,
        ...data.workOrderCalendarSetIds,
      ],
    };

    const response = await stateManager.run(async () => {
      return calendarService.tenantHolidays.update({
        id: data.id,
        data: payload,
        toastMessageConfig: {
          success: {
            text: `${data.name} updated successfully.`,
          },
          failure: {
            text: `Failed to update ${data.name}. Please try again.`,
          },
        },
      });
    });

    if (response?.status === HTTP_STATUSES.OK && data.id) {
      getHolidayDetails(data.id);
    }
  };

  useEffect(() => {
    fetchCalendarSets();
  }, []);

  return {
    getHolidayDetails,
    handleCreateHoliday,
    handleUpdateHoliday,
    orderCalendarSets,
    workOrderCalendarSets,
    isLoading: stateManager.isBusy(),
  };
};
